<template>
  <ClientOnly>
    <RevContextualMenu
      class="z-10"
      list-class-names="max-h-[390px]"
      :modifiers="[
        {
          name: 'offset',
          options: {
            offset: [16, 0],
          },
        },
      ]"
      position="top-start"
    >
      <template #trigger="{ show: showMenu, isOpen: isMenuOpen }">
        <RevTooltip
          v-if="isSmallScreen"
          :modifiers="[
            {
              name: 'offset',
              options: {
                offset: [0, -16],
              },
            },
          ]"
          position="right"
        >
          <template #trigger="{ show: showTooltip, hide: hideTooltip }">
            <RevButtonIcon
              :alternative-text="i18n(translations.label)"
              class="mx-16 my-12"
              :icon="CountryFlag"
              :loading="isLoading"
              variant="secondary"
              @click="show(showMenu, hideTooltip)"
              @focusin="!isMenuOpen && showTooltip()"
              @focusout="hideTooltip"
              @mouseenter="!isMenuOpen && showTooltip()"
              @mouseleave="hideTooltip"
            />
          </template>
          {{ currentLocaleLabel }}
        </RevTooltip>

        <RevButtonBase
          v-else
          :aria-label="i18n(translations.label)"
          class="text-action-default-hi my-9 flex w-full items-center px-24 py-[11px] pr-20 hover:bg-action-default-min-hover"
          :title="currentLocaleLabel"
          @click="showMenu"
        >
          <Component
            :is="isLoading ? RevSpinner : CountryFlag"
            class="mr-10 shrink-0"
          />
          <span class="truncate">
            {{ currentLocaleLabel }}
          </span>
          <IconChevronDown class="shrink-0" />
        </RevButtonBase>
      </template>

      <template #items="{ attrs }: any">
        <RevListItemInteractive
          v-for="option in options"
          :key="option.value"
          role="option"
          :wrapper-attrs="attrs"
          @click="handleUpdate(option.value)"
        >
          <template #label>
            <div class="flex gap-8">
              <RevCheckbox
                :id="option.value"
                class="w-20"
                :model-value="currentLocale === option.value"
              />
              {{ option.label }}
            </div>
          </template>
        </RevListItemInteractive>
      </template>
    </RevContextualMenu>
  </ClientOnly>
</template>

<script lang="ts" setup>
import { ref } from 'vue'

import type { Bcp47Locale } from '@backmarket/http-api'
import { changeLanguage } from '@backmarket/nuxt-module-i18n/changeLanguage'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useMarketplace } from '@backmarket/nuxt-module-marketplace/useMarketplace'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'
import { RevButtonBase } from '@ds/components/ButtonBase'
import { RevButtonIcon } from '@ds/components/ButtonIcon'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevContextualMenu } from '@ds/components/ContextualMenu'
import { RevListItemInteractive } from '@ds/components/ListItemInteractive'
import { RevSpinner } from '@ds/components/Spinner'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconChevronDown } from '@ds/icons/IconChevronDown'

import { useScreenSize } from '../../composables/useScreenSize'

import translations from './LanguageSelector.translations'
import CountryFlag from './components/CountryFlag/CountryFlag.vue'

const { allMarkets } = useMarketplace()

const locales = Object.values(allMarkets).map((market) => market.defaultLocale)

const i18n = useI18n()
const { isSmallScreen } = useScreenSize()

const isLoading = ref(false)

const options = locales.map((locale) => ({
  label: i18n.locale(toBcp47Locale(locale)),
  value: toBcp47Locale(locale),
}))

const currentLocale = useI18nLocale()
const currentLocaleLabel = i18n.locale(currentLocale)

async function show(showMenu: () => Promise<void>, hideTooltip: () => void) {
  hideTooltip()
  await showMenu()
}

function handleUpdate(locale: Bcp47Locale): void {
  changeLanguage(locale)
  isLoading.value = true
}
</script>
